/**
 * 文件上传重试和错误处理工具
 */

import { uploadFile, SimpleUploadConfig, UploadResult } from './uploadHelper';

/**
 * 重试配置
 */
export interface RetryConfig {
  maxRetries: number; // 最大重试次数
  retryDelay: number; // 重试延迟（毫秒）
  backoffMultiplier: number; // 退避乘数
}

/**
 * 默认重试配置
 */
const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  retryDelay: 1000, // 1秒
  backoffMultiplier: 2
};

/**
 * 带重试的文件上传
 * @param file 要上传的文件
 * @param config 上传配置
 * @param retryConfig 重试配置
 * @param onProgress 进度回调
 * @param onRetry 重试回调
 * @returns Promise<UploadResult>
 */
export const uploadFileWithRetry = async (
  file: File,
  config: SimpleUploadConfig,
  retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG,
  onProgress?: (percent: number) => void,
  onRetry?: (attempt: number, error: string) => void
): Promise<UploadResult> => {
  let lastError: string = '';
  
  for (let attempt = 1; attempt <= retryConfig.maxRetries + 1; attempt++) {
    try {
      const result = await uploadFile(file, config, onProgress);
      
      if (result.success) {
        return result;
      }
      
      lastError = result.error || '上传失败';
      
      // 如果不是最后一次尝试，进行重试
      if (attempt <= retryConfig.maxRetries) {
        if (onRetry) {
          onRetry(attempt, lastError);
        }
        
        // 计算延迟时间（指数退避）
        const delay = retryConfig.retryDelay * Math.pow(retryConfig.backoffMultiplier, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error) {
      lastError = error instanceof Error ? error.message : '上传异常';
      
      // 如果不是最后一次尝试，进行重试
      if (attempt <= retryConfig.maxRetries) {
        if (onRetry) {
          onRetry(attempt, lastError);
        }
        
        const delay = retryConfig.retryDelay * Math.pow(retryConfig.backoffMultiplier, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  return {
    success: false,
    error: `上传失败，已重试${retryConfig.maxRetries}次：${lastError}`
  };
};

/**
 * 上传状态管理器
 */
export class UploadStatusManager {
  private uploads: Map<string, {
    status: 'pending' | 'uploading' | 'success' | 'failed';
    progress: number;
    error?: string;
    result?: UploadResult;
  }> = new Map();
  
  private listeners: Map<string, Array<(status: any) => void>> = new Map();
  
  /**
   * 开始上传
   * @param uploadId 上传ID
   */
  startUpload(uploadId: string) {
    this.uploads.set(uploadId, {
      status: 'uploading',
      progress: 0
    });
    this.notifyListeners(uploadId);
  }
  
  /**
   * 更新上传进度
   * @param uploadId 上传ID
   * @param progress 进度百分比
   */
  updateProgress(uploadId: string, progress: number) {
    const upload = this.uploads.get(uploadId);
    if (upload) {
      upload.progress = progress;
      this.notifyListeners(uploadId);
    }
  }
  
  /**
   * 上传成功
   * @param uploadId 上传ID
   * @param result 上传结果
   */
  uploadSuccess(uploadId: string, result: UploadResult) {
    const upload = this.uploads.get(uploadId);
    if (upload) {
      upload.status = 'success';
      upload.progress = 100;
      upload.result = result;
      this.notifyListeners(uploadId);
    }
  }
  
  /**
   * 上传失败
   * @param uploadId 上传ID
   * @param error 错误信息
   */
  uploadFailed(uploadId: string, error: string) {
    const upload = this.uploads.get(uploadId);
    if (upload) {
      upload.status = 'failed';
      upload.error = error;
      this.notifyListeners(uploadId);
    }
  }
  
  /**
   * 获取上传状态
   * @param uploadId 上传ID
   * @returns 上传状态
   */
  getUploadStatus(uploadId: string) {
    return this.uploads.get(uploadId);
  }
  
  /**
   * 监听上传状态变化
   * @param uploadId 上传ID
   * @param listener 监听器
   */
  onStatusChange(uploadId: string, listener: (status: any) => void) {
    if (!this.listeners.has(uploadId)) {
      this.listeners.set(uploadId, []);
    }
    this.listeners.get(uploadId)!.push(listener);
  }
  
  /**
   * 移除监听器
   * @param uploadId 上传ID
   * @param listener 监听器
   */
  removeListener(uploadId: string, listener: (status: any) => void) {
    const listeners = this.listeners.get(uploadId);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }
  
  /**
   * 清理上传记录
   * @param uploadId 上传ID
   */
  cleanup(uploadId: string) {
    this.uploads.delete(uploadId);
    this.listeners.delete(uploadId);
  }
  
  private notifyListeners(uploadId: string) {
    const listeners = this.listeners.get(uploadId);
    const status = this.uploads.get(uploadId);
    if (listeners && status) {
      listeners.forEach(listener => listener(status));
    }
  }
}

// 全局上传状态管理器实例
export const globalUploadManager = new UploadStatusManager();